import React, { CSSProperties, FC, ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import qs from 'querystring';
import { Box, Flex, Button, Text, ButtonProps, Placeholder, Card, styled, css } from '@goodrx/matisse';

import pluralize from 'pluralize';
import { useAppContext } from 'app/config/AppContext';
import { truncateText } from 'app/lib/utils';
import { useSynapse } from 'app/lib/synaptik';
import { Coupon } from 'app/types/Coupon';
import { GetCoupon } from 'app/components/coupons/GetCoupon';
import { CouponNotFound } from 'app/components/coupons/CouponNotFound';
import { SendCouponCta } from 'app/components/coupons/SendCouponCta';

const GoodRxLogo = styled.img`
  width: 50px;
`;

const CouponMiniActionButton = styled(Button).attrs((p) => ({
  variant: `${p.isGoldClient ? 'secondary' : 'primary'}`,
}))`
  border-color: ${(p) => (p.isGoldClient ? p.theme?.colors.primaryLight : 'white')};
  color: ${(p) => (p.isGoldClient ? p.theme?.colors.primaryLight : 'white')};
`;

const NoCouponContainer = styled(Box)(
  (p) => css`
    max-width: 375px;
    padding: ${p.theme.space[3]}px;
  `
);

interface CouponMiniProps {
  coupon: Coupon;
  visitShortCode?: string;
  buttonVariant?: ButtonProps['variant'];
  visitPrescriptionId?: number;
  loading?: boolean;
  styles?: {
    couponCard: CSSProperties;
  };
}

export const CouponMini: FC<CouponMiniProps & ComponentProps<typeof Card>> = ({
  coupon,
  visitShortCode,
  buttonVariant,
  visitPrescriptionId,
  loading,
  styles,
  ...props
}) => {
  const [consentedToHma] = useSynapse(({ user }: any) => [user.consentedToHma()]);
  const { isGold } = useAppContext();

  if (!consentedToHma) return <GetCoupon />;

  return (
    <Card width={{ _: '100%', sm: 350 }} {...props} style={styles?.couponCard}>
      <Placeholder loading={loading}>
        {coupon ? (
          <Card.Body>
            <Flex flexDirection="column" flex={1}>
              <Flex>
                <Box flex={1}>
                  <Text fontWeight={600} as="p" m={0}>
                    {truncateText(coupon.display)}
                  </Text>
                  <Text color="greyDarker" fontWeight={500}>
                    {pluralize(coupon.form!, coupon.quantity, true)} - {coupon.dosage}
                  </Text>
                </Box>

                <Box>
                  <GoodRxLogo src="/images/logo-goodrx.png" />
                </Box>
              </Flex>

              <Link
                to={`/coupon?${qs.stringify({
                  ndc: coupon.ndc,
                  quantity: coupon.quantity,
                  ncpdp: coupon.pharmacy!.ncpdp,
                  visit_code: visitShortCode,
                })}`}
                target="_blank">
                <CouponMiniActionButton block outline mt={2} isGoldClient={isGold}>
                  Get Your Discount
                </CouponMiniActionButton>
              </Link>

              {visitPrescriptionId && (
                <SendCouponCta mt={3} visitPrescriptionId={visitPrescriptionId} title="Get Coupons On The Go" />
              )}
            </Flex>
          </Card.Body>
        ) : (
          <NoCouponContainer>
            <CouponNotFound />
          </NoCouponContainer>
        )}
      </Placeholder>
    </Card>
  );
};
